// pages/Documents/EditDocumentSummary.js
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import backendURL from "../../config";

// --- Tiptap Imports ---
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { marked } from 'marked';

// --- Ant Design Imports ---
import { Button as AntButton } from 'antd';


// Helper function to ensure URL has a protocol
const ensureUrlProtocol = (url) => {
  if (!url) return '';
  if (!/^https?:\/\//i.test(url) && !/^mailto:/i.test(url) && !/^tel:/i.test(url)) {
    return 'https://' + url;
  }
  return url;
};

// Toolbar component for the Tiptap editor
const TiptapToolbar = ({ editor }) => {
  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL:', previousUrl || '');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    const validatedUrl = ensureUrlProtocol(url);
    editor.chain().focus().extendMarkRange('link').setLink({ href: validatedUrl }).run();
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className="tiptap-toolbar p-2 border-bottom bg-light sticky-top d-flex align-items-center flex-wrap">
        <AntButton size="small" className="me-1" onClick={() => editor.chain().focus().toggleBold().run()} type={editor.isActive('bold') ? 'primary' : 'default'} title="Bold"><i className="fa fa-bold"></i></AntButton>
        <AntButton size="small" className="me-1" onClick={() => editor.chain().focus().toggleItalic().run()} type={editor.isActive('italic') ? 'primary' : 'default'} title="Italic"><i className="fa fa-italic"></i></AntButton>
        <AntButton size="small" className="me-1" onClick={() => editor.chain().focus().toggleUnderline().run()} type={editor.isActive('underline') ? 'primary' : 'default'} title="Underline"><i className="fa fa-underline"></i></AntButton>
        <AntButton size="small" className="me-1" onClick={() => editor.chain().focus().toggleStrike().run()} type={editor.isActive('strike') ? 'primary' : 'default'} title="Strike"><i className="fa fa-strikethrough"></i></AntButton>
        <AntButton size="small" className="me-1" onClick={setLink} type={editor.isActive('link') ? 'primary' : 'default'} title="Link"><i className="fa fa-link"></i></AntButton>
        <AntButton size="small" className="me-1" onClick={() => editor.chain().focus().toggleBulletList().run()} type={editor.isActive('bulletList') ? 'primary' : 'default'} title="Bullet List"><i className="fa fa-list-ul"></i></AntButton>
        <AntButton size="small" className="me-1" onClick={() => editor.chain().focus().toggleOrderedList().run()} type={editor.isActive('orderedList') ? 'primary' : 'default'} title="Ordered List"><i className="fa fa-list-ol"></i></AntButton>
        <AntButton size="small" className="me-1" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo"><i className="fa fa-undo"></i></AntButton>
        <AntButton size="small" className="me-1" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo"><i className="fa fa-repeat"></i></AntButton>
    </div>
  );
};


export default function EditDocumentSummary({ document, onClose, onUpdate }) {
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('save');

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false, // Important: disable default click behavior
        autolink: true,
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'tiptap-editor-area p-3 form-control',
      },
    },
  });

  useEffect(() => {
    if (document && editor) {
      const summaryAsHtml = document.summary ? marked.parse(document.summary) : "<p></p>";
      if (editor.getHTML() !== summaryAsHtml) {
        editor.commands.setContent(summaryAsHtml, false);
      }
    }
  }, [document, editor]);

  // <<< START: NEW CODE >>>
  // Handle Ctrl/Cmd + click on links to open in a new tab
  useEffect(() => {
    if (!editor || !editor.view.dom) return;

    const editorElement = editor.view.dom;
    const handleLinkClick = (event) => {
        const link = event.target.closest('a');
        if (link && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            window.open(link.href, '_blank', 'noopener, noreferrer');
        }
    };

    editorElement.addEventListener('click', handleLinkClick);

    return () => {
        editorElement.removeEventListener('click', handleLinkClick);
    };
  }, [editor]);
  // <<< END: NEW CODE >>>

  const handleSave = async () => {
    if (!editor) return;

    setIsSaving(true);
    setSaveStatus('saving');
    setError(null);

    try {
      const token = localStorage.getItem("access_token");
      if (!token) throw new Error("Authentication token not found.");
      
      const headers = { Authorization: `Bearer ${token}` };

      if (!document?.id) throw new Error("Document ID is missing.");
      
      const payload = {
        id: document.id,
        summary: editor.getHTML(), 
      };

      const response = await axios.post(
        `${backendURL}/edit-document-summary`,
        payload,
        { headers }
      );

      if (response.status === 200 && response.data.success) {
        setSaveStatus('saved');
        onUpdate();
        setTimeout(() => {
          onClose();
        }, 1200);
      } else {
         throw new Error(response.data.message || `Failed to update summary.`);
      }
    } catch (err)
     {
      const message = err.response?.data?.message || err.message || "Failed to update summary.";
      setError(message);
      setSaveStatus('save');
    } finally {
      setIsSaving(false);
    }
  };

  if (!editor) {
    return <div className="p-3">Loading editor...</div>;
  }

  return (
    <div className="edit-summary-content d-flex flex-column" style={{height: '100%', overflow:'hidden', backgroundColor: '#fff'}}>
      <div className="modal-header py-2 px-3 border-bottom">
        <h5 className="modal-title">Edit Summary for: {document?.filename}</h5>
        <button type="button" className="btn-close" aria-label="Close" onClick={onClose} disabled={isSaving}></button>
      </div>
      
      <TiptapToolbar editor={editor} />
      
      <div className="modal-body p-3" style={{flexGrow:1, overflowY:'auto'}}>
        {error && (
            <div className="alert alert-danger py-1 px-2 mb-2" role="alert" style={{fontSize: '0.85rem'}}>
                {error}
            </div>
        )}
        
        <div className="mb-3">
          <label htmlFor="summary-editor" className="form-label fw-bold">
            Document Summary:
          </label>
          <EditorContent editor={editor} id="summary-editor" />
          <small className="text-muted d-block mt-1">
                A brief summary of the document's content. This will be saved and will update the document's metadata for the chatbot.
          </small>
        </div>
      </div>

      <div className="modal-footer py-2 px-3 border-top">
          <AntButton onClick={onClose} disabled={isSaving} icon={<i className="fa fa-times" style={{ marginRight: '4px' }}></i>}>
             Cancel
          </AntButton>
          <AntButton 
            type="primary" 
            onClick={handleSave} 
            loading={isSaving} 
            icon={saveStatus === 'saving' ? null : (saveStatus === 'saved' ? <i className="fa fa-check"></i> : <i className="fa fa-save"></i>)}
            style={{minWidth: '100px'}}>
            {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved!' : 'Save Summary'}
          </AntButton>
      </div>

      <style jsx global>{`
        .tiptap-editor-area {
            min-height: 250px;
            max-height: calc(90vh - 300px);
            overflow-y: auto;
        }
        .ProseMirror { padding: 0.5rem; }
        .ProseMirror p { margin-bottom: 0.5rem; line-height: 1.5; }
        .ProseMirror ul, .ProseMirror ol { padding-left: 1.5rem; margin-bottom: 0.5rem; }
        .ProseMirror a { color: #0d6efd; text-decoration: underline; cursor: pointer; }
        .ProseMirror-focused {
            outline: none;
        }
      `}</style>
    </div>
  );
}